import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Text, Flex, Icon, Button, HStack, VStack, Table, Thead, Tbody, Tr, Th, Td, Badge, Avatar, IconButton, SimpleGrid, Circle } from "@chakra-ui/react";
import { Calendar, Users, ChevronLeft, ChevronRight, Clock, LucideIcon } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipProps } from "recharts";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { dashboardService, Doctor7DaysSummary } from "@/lib/services/dashboardService";
import { Appointment } from "@/types/appointment";
import { formatDateTime } from "@/util/convertTime";

import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, format, isSameDay, isSameMonth, isToday, isPast, parseISO, startOfDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { getStatusColor } from "@/util/getStatusColor";
import { CalendarSkeleton, ChartSkeleton, ComparisonSkeleton, shimmerAnimation, StatCardSkeleton, TableSkeleton } from "@/components/loading/DoctorDashboardSkeleton";

interface StatCard {
  label: string;
  value: keyof DashboardSummary;
  days: string;
  icon: LucideIcon;
  color: string;
}

interface AppointmentChartData {
  month: string;
  completed: number;
  cancelled: number;
  rescheduled: number;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

interface DashboardSummary {
  total_patients: number;
  total_appointments: number;
  pending_appointments: number;
  completed_appointments: string;
}

interface Statistics {
  total_appointments: number;
  cancelled: number;
  completed: number;
  rescheduled: number;
}

const statCards: StatCard[] = [
  { label: "Patients", value: "total_patients", days: "7 Days", icon: Users, color: "red.500" },
  { label: "Appointment", value: "total_appointments", days: "7 Days", icon: Calendar, color: "cyan.500" },
  { label: "Pending Appointments", value: "pending_appointments", days: "7 Days", icon: Clock, color: "yellow.500" },
  { label: "Completed Appointments", value: "completed_appointments", days: "7 Days", icon: Calendar, color: "teal.600" },
];

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <Box bg="white" p={3} border="1px" borderColor="gray.200" rounded="md" shadow="lg">
      <Text fontWeight="bold" mb={2}>
        {label}
      </Text>
      {payload.map((entry, index) => (
        <Flex key={index} justify="space-between" gap={4}>
          <HStack spacing={2}>
            <Box w={3} h={3} bg={entry.color} rounded="sm" />
            <Text fontSize="sm">{entry.name}:</Text>
          </HStack>
          <Text fontSize="sm" fontWeight="semibold">
            {entry.value}
          </Text>
        </Flex>
      ))}
      <Flex justify="space-between" gap={4} mt={2} pt={2} borderTop="1px" borderColor="gray.200">
        <Text fontSize="sm" fontWeight="bold">
          Total:
        </Text>
        <Text fontSize="sm" fontWeight="bold">
          {total}
        </Text>
      </Flex>
    </Box>
  );
};

export default function DoctorsDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [appointmentChartData, setAppointmentChartData] = useState<AppointmentChartData[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]);
  const [summary, setSummary] = useState<DashboardSummary>({
    total_patients: 0,
    total_appointments: 0,
    pending_appointments: 0,
    completed_appointments: "₱0",
  });
  const [summary7Days, setSummary7Days] = useState<Doctor7DaysSummary>({
    total_patients: { daily: [], change: 0 },
    total_appointments: { daily: [], change: 0 },
    pending_appointments: { daily: [], change: 0 },
    completed_appointments: { daily: [], change: 0 },
  });
  const [statistics, setStatistics] = useState<Statistics>({
    total_appointments: 0,
    cancelled: 0,
    completed: 0,
    rescheduled: 0,
  });
  const [appointmentYesterday, setAppointmentYesterday] = useState(0);
  const [appointmentToday, setAppointmentToday] = useState(0);

  const totalAppointments = appointmentYesterday + appointmentToday;
  const yesterdayPercentage = totalAppointments > 0 ? (appointmentYesterday / totalAppointments) * 100 : 0;
  const todayPercentage = totalAppointments > 0 ? (appointmentToday / totalAppointments) * 100 : 0;

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth]);

  const appointmentDates = useMemo(() => {
    if (upcomingAppointments) {
      return new Set(upcomingAppointments.map((apt) => format(parseISO(apt.appointment_date), "yyyy-MM-dd")));
    } else {
      return new Set();
    }
  }, [upcomingAppointments]);

  const filteredUpcomingAppointments = useMemo(() => {
    if (!selectedDate) return upcomingAppointments;

    if (upcomingAppointments) {
      return upcomingAppointments.filter((apt) => {
        const aptDate = parseISO(apt.appointment_date);
        return isSameDay(aptDate, selectedDate);
      });
    } else {
      return [];
    }
  }, [upcomingAppointments, selectedDate]);

  const handlePrevMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    const today = startOfDay(new Date());
    const newMonthStart = startOfMonth(newMonth);

    if (newMonthStart >= startOfMonth(today)) {
      setCurrentMonth(newMonth);
    }
  };

  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDateClick = (date: Date) => {
    const today = startOfDay(new Date());
    if (startOfDay(date) >= today) {
      setSelectedDate(date);
    }
  };
  const canGoPrevMonth = useMemo(() => {
    const today = new Date();
    const prevMonth = subMonths(currentMonth, 1);
    return startOfMonth(prevMonth) >= startOfMonth(today);
  }, [currentMonth]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await dashboardService.getDoctorDashboard();

        setAllAppointments(data.all_appointments);
        setUpcomingAppointments(data.upcoming_appointments);
        setAppointmentChartData(data.statistics.per_month);

        setStatistics({
          total_appointments: data.statistics.total_appointments,
          cancelled: data.statistics.cancelled,
          completed: data.statistics.completed,
          rescheduled: data.statistics.rescheduled,
        });

        setSummary({
          total_patients: data.summary.total_patients,
          total_appointments: data.summary.total_appointments,
          pending_appointments: data.summary.pending_appointments,
          completed_appointments: `₱${data.summary.completed_appointments}`,
        });

        setSummary7Days(data.summary.last_7_days);
        setAppointmentToday(data.statistics.appointments_today);
        setAppointmentYesterday(data.statistics.appointments_yesterday);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <AdminLayout>
      <style>{shimmerAnimation}</style>
      <Box minH="calc(100vh - 110px)" overflowX="hidden" display="flex" flexDirection="column" bg="#FEFCF8" py={1} px={5}>
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Doctor's Dashboard
        </Text>

        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
          {isLoading ? (
            <>
              {statCards.map((_, index) => (
                <StatCardSkeleton key={index} />
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Box key={index} bg="white" rounded="lg" shadow="sm" p={4}>
                <Flex justify="space-between" mb={3}>
                  <Circle bg={stat.color} p={3}>
                    <Icon as={stat.icon} boxSize={5} color="white" />
                  </Circle>
                  <Box textAlign="right">
                    <Badge colorScheme="green" fontSize="xs" mb={1} rounded="md">
                      {summary7Days[stat.value].change}%
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      in last {stat.days}
                    </Text>
                  </Box>
                </Flex>
                <Text color="gray.600" fontSize="sm" mb={1}>
                  {stat.label}
                </Text>
                <Flex justify="space-between" align="flex-end">
                  <Text fontSize="2xl" fontWeight="bold">
                    {summary[stat.value]}
                  </Text>
                  <Flex align="flex-end" gap={0.5} h="32px">
                    {(() => {
                      const dailyValues = summary7Days[stat.value].daily;
                      const maxValue = Math.max(...dailyValues, 1);
                      return dailyValues.map((value, i) => {
                        const normalizedHeight = (value / maxValue) * 100;
                        return (
                          <Box 
                            key={i} 
                            w="6px" 
                            h={`${normalizedHeight}%`} 
                            minH="4px"
                            bgGradient="linear(to-t, cyan.500, purple.500)" 
                            roundedTop="sm" 
                          />
                        );
                      });
                    })()}
                  </Flex>
                </Flex>
              </Box>
            ))
          )}
        </Grid>

        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={6}>
          <Flex direction="column" gap={6}>
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <Box bg="white" rounded="lg" shadow="sm" p={6}>
                <Flex justify="space-between" align="center" mb={6}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Appointment Statistics
                  </Text>
                </Flex>

                <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
                  {[
                    { label: "All Appointments", value: statistics.total_appointments, color: "cyan.400" },
                    { label: "Cancelled", value: statistics.cancelled, color: "red.400" },
                    { label: "Reschedule", value: statistics.rescheduled, color: "purple.600" },
                    { label: "Completed", value: statistics.completed, color: "teal.400" },
                  ].map((item, idx) => (
                    <Box key={idx} bg="gray.50" rounded="lg" p={4} textAlign="center">
                      <HStack spacing={2} justify="center" mb={2}>
                        <Box w={2} h={2} bg={item.color} rounded="full" />
                        <Text fontSize="xs" color="gray.600">
                          {item.label}
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold">
                        {item.value}
                      </Text>
                    </Box>
                  ))}
                </Grid>

                <Box h="200px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={appointmentChartData} margin={{ top: 20, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#718096", fontSize: 12 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: "#718096", fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0, 0, 0, 0.05)" }} />
                      <Bar dataKey="completed" stackId="a" fill="#38B2AC" radius={[0, 0, 4, 4]} />
                      <Bar dataKey="cancelled" stackId="a" fill="#F56565" />
                      <Bar dataKey="rescheduled" stackId="a" fill="#6B46C1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <Flex justify="center" gap={8} mt={4}>
                  {[
                    { label: "Completed", color: "teal.400" },
                    { label: "Cancelled", color: "red.400" },
                    { label: "Rescheduled", color: "purple.600" },
                  ].map((item, idx) => (
                    <HStack key={idx} spacing={2}>
                      <Box w={3} h={3} bg={item.color} rounded="sm" />
                      <Text fontSize="sm" color="gray.600">
                        {item.label}
                      </Text>
                    </HStack>
                  ))}
                </Flex>
              </Box>
            )}

            {isLoading ? (
              <ComparisonSkeleton />
            ) : (
              <Box bg="white" rounded="lg" shadow="sm" p={6}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Patients Seen Today vs Patients Seen Yesterday
                  </Text>
                </Flex>

                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                  {[
                    { label: "Appointment Yesterday", value: appointmentYesterday, color: "purple.600" },
                    { label: "Appointment Today", value: appointmentToday, color: "cyan.400" },
                  ].map((item, idx) => (
                    <Box key={idx} bg="gray.50" rounded="lg" p={4} textAlign="center">
                      <HStack spacing={2} justify="center" mb={2}>
                        <Box w={2} h={2} bg={item.color} rounded="full" />
                        <Text fontSize="xs" color="gray.600">
                          {item.label}
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold">
                        {item.value}
                      </Text>
                    </Box>
                  ))}
                </Grid>

                <Flex h={16} rounded="lg" overflow="hidden" gap={2}>
                  <Flex bg="purple.600" align="center" justify="center" color="white" fontWeight="semibold" w={`${yesterdayPercentage}%`} fontSize="sm">
                    {yesterdayPercentage && "Total of Appointment Yesterday"}
                  </Flex>
                  <Flex bg="cyan.400" align="center" justify="center" color="white" fontWeight="semibold" w={`${todayPercentage}%`} fontSize="sm">
                    {todayPercentage && "Total of Appointment Today"}
                  </Flex>
                </Flex>

                <Flex justify="center" gap={8} mt={4}>
                  {[
                    { label: "Total of Appointment Yesterday", color: "purple.600" },
                    { label: "Total of Appointment Today", color: "cyan.400" },
                  ].map((item, idx) => (
                    <HStack key={idx} spacing={2}>
                      <Box w={3} h={3} bg={item.color} rounded="sm" />
                      <Text fontSize="sm" color="gray.600">
                        {item.label}
                      </Text>
                    </HStack>
                  ))}
                </Flex>
              </Box>
            )}
          </Flex>

          {isLoading ? (
            <CalendarSkeleton />
          ) : (
            <Box bg="white" rounded="lg" shadow="sm" p={5} display="flex" flexDirection="column">
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Upcoming Appointments
                </Text>
              </Flex>

              <Box mb={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <IconButton aria-label="Previous month" icon={<ChevronLeft />} size="sm" variant="ghost" onClick={handlePrevMonth} isDisabled={!canGoPrevMonth} />
                  <Text fontWeight="bold" fontSize="md">
                    {format(currentMonth, "MMMM yyyy")}
                  </Text>
                  <IconButton aria-label="Next month" icon={<ChevronRight />} size="sm" variant="ghost" onClick={handleNextMonth} />
                </Flex>

                <SimpleGrid columns={7} spacing={1} mb={2}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, idx) => (
                    <Text key={idx} fontSize="xs" fontWeight="semibold" color="gray.700" textAlign="center">
                      {day}
                    </Text>
                  ))}
                </SimpleGrid>

                <SimpleGrid columns={7} spacing={1}>
                  {calendarDays.map((day, index) => {
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isTodayDate = isToday(day);
                    const hasAppointment = appointmentDates.has(format(day, "yyyy-MM-dd"));
                    const isPastDate = isPast(startOfDay(day)) && !isTodayDate;

                    return (
                      <Flex
                        key={index}
                        align="center"
                        justify="center"
                        h="32px"
                        fontSize="sm"
                        fontWeight={isSelected ? "bold" : "normal"}
                        color={isPastDate ? "gray.200" : !isCurrentMonth ? "gray.300" : isSelected ? "white" : "gray.700"}
                        bg={isSelected ? "orange.500" : isTodayDate ? "blue.50" : "transparent"}
                        rounded="md"
                        cursor={isPastDate ? "not-allowed" : "pointer"}
                        onClick={() => !isPastDate && handleDateClick(day)}
                        position="relative"
                        _hover={{ bg: isPastDate ? "transparent" : isSelected ? "orange.500" : "gray.100" }}
                        border={isTodayDate && !isSelected ? "1px solid" : "none"}
                        borderColor="blue.400"
                        opacity={isPastDate ? 0.4 : 1}
                      >
                        {format(day, "d")}
                        {hasAppointment && isCurrentMonth && !isPastDate && <Circle size="4px" bg={isSelected ? "white" : "orange.500"} position="absolute" bottom="4px" />}
                      </Flex>
                    );
                  })}
                </SimpleGrid>
              </Box>

              <VStack spacing={2} align="stretch" flex={1}>
                {filteredUpcomingAppointments.length > 0 ? (
                  filteredUpcomingAppointments.map((apt, index) => (
                    <Flex key={index} align="center" gap={3} p={3} bg="gray.50" rounded="lg" _hover={{ bg: "gray.100" }} onClick={() => navigate(`/appointments/view-appointment/${apt.id}`)}>
                      <Avatar size="sm" bg="gray.200" />
                      <Box flex={1}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {apt.patient?.user_fullname ?? "-"}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {apt.service ?? "-"}
                        </Text>
                        <HStack spacing={1} fontSize="xs" color="gray.500">
                          <Icon as={Clock} boxSize={3} />
                          <Text>{apt.appointment_time}</Text>
                        </HStack>
                      </Box>
                    </Flex>
                  ))
                ) : (
                  <Flex justify="center" align="center" flex={1} color="gray.400">
                    <Text fontSize="sm">No appointments for this date</Text>
                  </Flex>
                )}
              </VStack>

              <Button w="full" mt={4} size="sm" variant="ghost" fontWeight="medium" color="gray.700">
                View All Appointments
              </Button>
            </Box>
          )}
        </Grid>

        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Box bg="white" rounded="lg" shadow="sm" overflow="hidden">
            <Flex p={6} borderBottom="1px" borderColor="gray.200" justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="semibold">
                All Appointments
              </Text>
              <Button size="sm" colorScheme="blue" variant="ghost" fontWeight="medium" onClick={() => navigate("/appointments")}>
                View All
              </Button>
            </Flex>

            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Patient</Th>
                    <Th>Date & Time</Th>
                    <Th>Service</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {allAppointments.map((appointment, index) => (
                    <Tr key={index} _hover={{ bg: "gray.50" }}>
                      <Td>
                        <HStack spacing={3}>
                          <Avatar
                            display="inline-flex"
                            size={{ base: "xs", lg: "sm" }}
                            name={appointment.patient.user_fullname}
                            src={appointment.patient.image?.file_url || "/assets/img/profile.jpg"}
                            bg="blue.100"
                            color="blue.700"
                            fontSize={{ base: "xs", lg: "sm" }}
                          />
                          <Box>
                            <Text fontWeight="medium" fontSize="sm">
                              {appointment.patient.user_fullname ?? "-"}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {appointment.patient.user_phone_number ?? "-"}
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td fontSize="sm">{formatDateTime(appointment.appointment_date)}</Td>
                      <Td fontSize="sm">{appointment.service ?? "-"}</Td>
                      <Td>
                        <Badge
                                  colorScheme={getStatusColor(
                                    appointment.status,
                                  )}
                                  fontSize={{ base: "8px", lg: "xs" }}
                                  px={{ base: 1, lg: 2 }}
                                  borderRadius="md"
                                  variant="outline"
                                >
                                  {appointment.status}
                                </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
}